/**
 * The platform's sign-in providers: ONE list (2026-10-02).
 *
 * WHY THIS FILE EXISTS. "Which providers can a person sign in with" was
 * written down in eight places, each with its own copy: the host hand-off
 * (`HANDOFF_PROVIDERS`), the verified-email mark (`IDENTITY_PROVIDERS`), the
 * login body's start URLs, its button order, its auto-start expression and
 * its in-app browser sheet, the account-choice page's label, and two more
 * link pairs on the authorize pages. They agreed only because nobody had
 * added a provider since Discord. LinkedIn is the fourth
 * (docs/internal/architecture/app-sign-in-and-onboarding.md, section 5), and
 * an app may now narrow and reorder the list for its own sign-in screen
 * (section 4), so the next copy that drifts decides which button a person
 * sees. Everything reads this module instead.
 *
 * WHAT IT IS NOT. It does not know whether a provider is CONFIGURED on this
 * environment (that is a public value in the runtime config, computed
 * server-side from the presence of each provider's credentials), and it does
 * not decide how many buttons a screen shows before "Other ways to sign in"
 * (that is the screen's). Passkeys are not a provider and are not here.
 *
 * Pure and dependency-free on purpose: the login body (client), the start
 * and callback routes (server) and the host hand-off all import it.
 */

/** Every provider the platform can sign a person in with, in the platform's default order. */
export const SIGN_IN_PROVIDERS = ['google', 'x', 'discord', 'linkedin'] as const;
export type SignInProvider = (typeof SIGN_IN_PROVIDERS)[number];

export function isSignInProvider(value: unknown): value is SignInProvider {
  return typeof value === 'string' && (SIGN_IN_PROVIDERS as readonly string[]).includes(value);
}

export interface SignInProviderInfo {
  /** The apex route that starts the provider's flow. */
  startPath: string;
  /** The button's label, a key in the translations. */
  labelKey: string;
  /** The brand mark (simple-icons slug, the sanctioned exception to lucide). */
  iconSlug: string;
}

export const SIGN_IN_PROVIDER_INFO: Readonly<Record<SignInProvider, SignInProviderInfo>> = {
  google: { startPath: '/api/auth/google', labelKey: 'auth.continueWithGoogle', iconSlug: 'google' },
  x: { startPath: '/api/auth/x', labelKey: 'auth.continueWithX', iconSlug: 'x' },
  discord: { startPath: '/api/auth/discord', labelKey: 'auth.continueWithDiscord', iconSlug: 'discord' },
  linkedin: { startPath: '/api/auth/linkedin', labelKey: 'auth.continueWithLinkedIn', iconSlug: 'linkedin' },
};

/**
 * The providers one screen offers, in order.
 *
 * `requested` is an app's own list (its bundle declaration, or a constant
 * for a first-party app): it may only NARROW and REORDER. A value that is
 * not a platform provider is dropped, a repeat is dropped, and a list with
 * nothing left falls back to the platform's, so an app can never add a
 * provider the platform lacks or leave a person with no button. `available`
 * is what this environment has credentials for; a provider that is not
 * available is left out whichever list named it. Hostile input by design:
 * `requested` comes from an app, which is user space.
 */
export function signInProvidersFor(
  requested: readonly unknown[] | null | undefined,
  available: readonly unknown[] | null | undefined = SIGN_IN_PROVIDERS,
): SignInProvider[] {
  const can = new Set((available ?? []).filter(isSignInProvider));
  const pick = (list: readonly unknown[]): SignInProvider[] => {
    const out: SignInProvider[] = [];
    for (const v of list) {
      if (isSignInProvider(v) && can.has(v) && !out.includes(v)) out.push(v);
    }
    return out;
  };
  const own = Array.isArray(requested) ? pick(requested) : [];
  return own.length ? own : pick(SIGN_IN_PROVIDERS);
}
